import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  MembershipCancelReason,
  MembershipCancelReasonsService,
  ParentMembershipCancelService,
  ParentPlan,
  ParentPlanService,
  WinbackCode,
  WinbackCodeService,
} from 'us-web-services';
import { getLongDateString } from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const OTHER_CODE = 'cancel_other';

export const CancelReasons = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const [activePlan, setActivePlan] = useState<ParentPlan | null>(null);
  const [reasons, setReasons] = useState<MembershipCancelReason[]>([]);
  const [winback, setWinback] = useState<WinbackCode | null>(null);
  const [selected, setSelected] = useState<MembershipCancelReason | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Cancel Membership';
  }, []);

  useEffect(() => {
    tracker.track('change-membership', { action: 'page-viewed' });

  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const loadPlan = async (): Promise<void> => {
      try {
        const resp = await ParentPlanService.getByFilter({
          params: { active: true, userId: user.id },
        });
        if (cancelled) return;
        const list = (resp.data?.data ?? []) as ParentPlan[];
        if (list.length) {
          setActivePlan(list[0]);
        } else {
          navigate('/settings', { replace: true });
        }
      } catch (err) {
        console.warn('[CancelReasons] plan load failed', err);
      }
    };

    const loadReasons = async (): Promise<void> => {
      try {
        const resp = await MembershipCancelReasonsService.getByFilter({
          params: { id: user.id, active: true },
        });
        if (cancelled) return;
        const list = resp.data?.data ?? [];
        setReasons(list);
      } catch (err) {
        console.warn('[CancelReasons] reasons load failed', err);
      }
    };

    const loadWinback = async (): Promise<void> => {
      try {
        const resp = await WinbackCodeService.getByFilter({ params: { active: true } });
        if (cancelled) return;
        const list = resp.data?.data ?? [];
        setWinback(list.length ? list[list.length - 1] : null);
      } catch (err) {
        console.warn('[CancelReasons] winback load failed', err);
      }
    };

    void (async (): Promise<void> => {
      await Promise.allSettled([loadPlan(), loadReasons(), loadWinback()]);
      if (!cancelled) setLoading(false);
    })();

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, navigate]);

  const textRequired = selected?.code === OTHER_CODE;
  const canSubmit =
    !!selected &&
    !submitting &&
    (!textRequired || reasonText.trim().length > 0);

  const planEndDate = getLongDateString(activePlan?.end);

  const goNext = (): void => {
    if (winback) {
      navigate('/cancel-complete');
    } else if (user?.id) {
      navigate(`/parent/${user.id}/settings`);
    } else {
      navigate('/');
    }
  };

  const submit = async (): Promise<void> => {
    if (!canSubmit || !user?.id || !activePlan?.plan?.id || !selected) return;
    setSubmitting(true);
    tracker.track('change-membership', {
      action: 'cancelation-reason-selected',
      elementName: selected.label,
    });
    try {
      await ParentMembershipCancelService.create({
        parentId: user.id,
        reason: selected,
        cancelText: reasonText,
        planId: activePlan.plan.id,
      });
      toast.success(t('pages.cancelReasons.submitSuccess'));
      goNext();
    } catch {
      toast.error(t('pages.cancelReasons.submitFailed'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className='container py-8 max-w-2xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-2'>{t('pages.cancelReasons.heading')}</h1>
      <p className='mb-6'>
        {t('pages.cancelReasons.retainAccessPrefix')}{' '}
        <b>{planEndDate}</b>
        {t('pages.cancelReasons.retainAccessSuffix')}
      </p>

      <h3 className='text-lg font-semibold mb-1'>{t('pages.cancelReasons.pleaseTellUs')}</h3>
      <p className='mb-4 text-sm'>{t('pages.cancelReasons.chooseOrWrite')}</p>

      <div className='space-y-2 mb-4'>
        {reasons.map((reason) => {
          const isSelected = selected?.id === reason.id;
          return (
            <Button
              key={reason.id}
              id={`cancel-reason-${reason.id}`}
              buttonType={isSelected ? ButtonType.primary : ButtonType.secondary}
              onClick={(): void => setSelected(reason)}
              className={`block w-full text-left rounded border px-4 py-3 transition ${
                isSelected
                  ? 'border-sapphire bg-sapphire-50'
                  : 'border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <span className='font-medium text-sapphire'>{reason.label}</span>
            </Button>
          );
        })}
      </div>

      <textarea
        rows={4}
        value={reasonText}
        onChange={(e): void => setReasonText(e.target.value)}
        placeholder={
          textRequired
            ? t('pages.cancelReasons.textareaRequired')
            : t('pages.cancelReasons.textareaOptional')
        }
        className='w-full border border-neutral-300 rounded p-2 mb-6 focus:ring-sapphire focus:border-sapphire'
      />

      <div className='flex gap-3'>
        <Button
          id='skip-reason'
          buttonType={ButtonType.secondary}
          onClick={goNext}
          disabled={submitting}
        >
          {t('pages.cancelReasons.skip')}
        </Button>
        <Button
          id='submit-cancel-reason'
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={!canSubmit}
        >
          {t('pages.cancelReasons.submit')}
        </Button>
      </div>
    </div>
  );
});
